import React, { useEffect } from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { IFarmFormValues } from '../../../../@types/model/masterData/farm/farmFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledMuiAutoCompleteSelect, ControlledTextField } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';
import { yupResolver } from '@hookform/resolvers/yup';
import FarmModelHelper from '../../../../@types/model/masterData/farm/farmModelHelper';
import OptionType from '../../../../@types/model/optionType';

interface IFarmFormProps {
    isLoading : boolean;
    initialValues : IFarmFormValues;
    onSubmit : (values : IFarmFormValues) => void;
    onClose : () => void;
}

const FarmForm = (props : IFarmFormProps) : React.ReactElement => {
    const organizations = useAppSelector(x => x.masterData.organizations);
    const contactInfos = useAppSelector(x => x.masterData.contactInfos);
    const accreditations = useAppSelector(x => x.masterData.accreditations);
    const ftpDetails = useAppSelector(x => x.masterData.ftpDetails);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<IFarmFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(FarmModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const organizationOptions = useMemo(() => {
        return organizations.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [organizations]);

    const contactInfoOptions = useMemo(() => {
        return contactInfos.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [contactInfos]);

    const accreditationOptions = useMemo(() => {
        return accreditations.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [accreditations]);

    const ftpDetailOptions = useMemo(() => {
        return ftpDetails.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [ftpDetails]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(props.onSubmit)} className={'fdc p20'}>
                <div className={'fdr mb15'}>
                    <ControlledTextField
                        className={'wfill'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <div className={'w20'}/>
                    <ControlledTextField
                        className={'wfill'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                </div>
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'extendedDescription'}
                    label={'Extended Description'}
                    control={control}
                    defaultValue={props.initialValues.extendedDescription}
                    size={'small'}
                />
                <ControlledTextField
                    className={'mb15 wfill'}
                    name={'address'}
                    label={'Address'}
                    control={control}
                    defaultValue={props.initialValues.address}
                    multiline
                    rows={5}
                    size={'small'}
                />
                <div className={'fdr mb15'}>
                    <ControlledTextField
                        className={'wfill'}
                        name={'telNumber'}
                        label={'Telephone Number'}
                        control={control}
                        defaultValue={props.initialValues.telNumber}
                        size={'small'}
                    />
                    <div className={'w20'}/>
                    <ControlledTextField
                        className={'wfill'}
                        name={'faxNumber'}
                        label={'Fax Number'}
                        control={control}
                        defaultValue={props.initialValues.faxNumber}
                        size={'small'}
                    />
                </div>
                <div className={'fdr mb15'}>
                    <ControlledTextField
                        className={'wfill'}
                        name={'globalGapNumber'}
                        label={'Global Gap Number (GGN)'}
                        control={control}
                        defaultValue={props.initialValues.globalGapNumber}
                        size={'small'}
                    />
                    <div className={'w20'}/>
                    <ControlledTextField
                        className={'wfill'}
                        name={'vatNumber'}
                        label={'Vat Number'}
                        control={control}
                        defaultValue={props.initialValues.vatNumber}
                        size={'small'}
                    />
                </div>
                <div className={'fdr mb15'}>
                    <ControlledTextField
                        className={'wfill'}
                        name={'bankName'}
                        label={'Bank Name'}
                        control={control}
                        defaultValue={props.initialValues.bankName}
                        size={'small'}
                    />
                    <div className={'w20'}/>
                    <ControlledTextField
                        className={'wfill'}
                        name={'bankAccount'}
                        label={'Bank Account'}
                        control={control}
                        defaultValue={props.initialValues.bankAccount}
                        size={'small'}
                    />
                </div>
                <div className={'fdr mb10'}>
                    <ControlledTextField
                        className={'wfill'}
                        name={'exportedCode'}
                        label={'Exported Code'}
                        control={control}
                        defaultValue={props.initialValues.exportedCode}
                        size={'small'}
                    />
                    <div className={'w20'}/>
                    <ControlledTextField
                        className={'wfill'}
                        name={'exportedDescription'}
                        label={'Exported Description'}
                        control={control}
                        defaultValue={props.initialValues.exportedDescription}
                        size={'small'}
                    />
                </div>
                <div className={'fdr'}>
                    <ControlledMuiAutoCompleteSelect
                        className={'wfill'}
                        name={'accreditation'}
                        label={'Accreditation'}
                        control={control}
                        options={accreditationOptions}
                        defaultValues={props.initialValues.accreditation ?? null}
                    />
                    <div className={'w20'}/>
                    <ControlledMuiAutoCompleteSelect
                        className={'wfill'}
                        name={'organizations'}
                        label={'Organizations'}
                        control={control}
                        options={organizationOptions}
                        defaultValues={props.initialValues.organizations}
                        isMulti
                    />
                </div>
                <div className={'fdr mb10'}>
                    <ControlledMuiAutoCompleteSelect
                        className={'wfill'}
                        name={'ftpDetails'}
                        label={'FtpDetails'}
                        control={control}
                        options={ftpDetailOptions}
                        defaultValues={props.initialValues.ftpDetails}
                        isMulti
                    />
                    <div className={'w20'}/>
                    <ControlledMuiAutoCompleteSelect
                        className={'wfill'}
                        name={'contactInfos'}
                        label={'Contact Info'}
                        control={control}
                        options={contactInfoOptions}
                        defaultValues={props.initialValues.contactInfos}
                        isMulti
                    />
                </div>
                <div className={'fdr jcfs'}>
                    <ControlledLabelledCheckBox
                        name={'isActive'}
                        label={'Is Active'}
                        control={control}
                        defaultValue={props.initialValues.isActive}
                        disabled={props.initialValues.isActive}
                    />
                </div>
                <div className={'fdr flx1 aic jcfe'}>
                    <Button 
                        className={'cpd mr20'}
                        onClick={props.onClose} 
                        type={'reset'}>
                        CANCEL
                    </Button>
                    <PillButton
                        className={'pl30 pr30'}
                        text={'SAVE'}
                        size={'small'}
                        type={'submit'}
                        color={'secondary'}
                        disabled={ !isDirty || !isValid || props.isLoading }
                    />
                </div>
            </form>
        </div>
    );
};

export default FarmForm;
